import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { closeTopDropdown } from '../../utils/dropdownEscapeStack';

export interface CpmModalProps {
    open: boolean;
    onClose: () => void;
    maxWidth?: number;
    children: ReactNode;
    zIndex?: number;
    /**
     * `card` — CPM review-detail pop (top-aligned).
     * `page` — full-viewport page modal.
     * `dialog` — Reports Admin prototype: centred white panel, dialog shadow, no border.
     */
    variant?: 'card' | 'page' | 'dialog';
    /**
     * When false, Escape does not close this layer.
     * Used so stacked modals only dismiss the top dialog.
     */
    closeOnEscape?: boolean;
    /** When false, backdrop click does not close (underlying stacked layers). */
    closeOnBackdrop?: boolean;
    /**
     * When false, the dimmed/blurred backdrop is omitted so stacked layers
     * do not double-tint the page — only the top modal dims.
     */
    showBackdrop?: boolean;
    /** Fixed panel height (e.g. copy-from dialog `min(560px, 100%)`). */
    height?: number | string;
}

/**
 * CPM modal shell — portals to document.body so the dialog covers the full
 * viewport above Layout. Overlay root stays transparent; `.cpm-theme` lives
 * on the dialog only so page-bg does not paint a solid full-screen fill.
 */
export default function CpmModal({
    open,
    onClose,
    maxWidth = 760,
    children,
    zIndex = 60,
    variant = 'card',
    closeOnEscape = true,
    closeOnBackdrop = true,
    showBackdrop = true,
    height,
}: CpmModalProps) {
    useEffect(() => {
        if (!open || !closeOnEscape) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (closeTopDropdown()) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return;
            }
            onClose();
        };
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
        };
    }, [open, onClose, closeOnEscape]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;
    if (typeof document === 'undefined') return null;

    const isPage = variant === 'page';
    const isDialog = variant === 'dialog';

    return createPortal(
        <div
            className={[
                'fixed inset-0 flex justify-center overflow-hidden overscroll-none',
                isPage || isDialog ? 'items-center' : 'items-start',
                !showBackdrop ? 'pointer-events-none' : '',
            ].join(' ')}
            style={{ zIndex, padding: isDialog ? '40px 24px' : isPage ? 32 : 24 }}
        >
            {showBackdrop ? (
                <button
                    type="button"
                    aria-label="Close overlay"
                    className={[
                        'absolute inset-0 border-0 cursor-default',
                        isDialog ? '' : 'backdrop-blur-[3px]',
                    ].join(' ')}
                    style={{
                        background: isDialog
                            ? 'rgba(14, 8, 88, 0.28)'
                            : isPage
                              ? 'rgba(14, 8, 88, 0.38)'
                              : 'rgba(14, 8, 88, 0.34)',
                    }}
                    onClick={closeOnBackdrop ? onClose : undefined}
                    onWheel={(e) => e.preventDefault()}
                    onTouchMove={(e) => e.preventDefault()}
                />
            ) : null}
            <div
                role="dialog"
                aria-modal="true"
                className={[
                    'cpm-theme relative w-full rounded-[var(--cpm-radius-2xl)] overflow-hidden',
                    isDialog
                        ? 'flex flex-col cpm-animate-pop-in'
                        : [
                              'border border-[var(--cpm-border-default)] cpm-animate-pop-in',
                              isPage
                                  ? 'shadow-[0_1px_2px_rgba(14,8,88,0.06),0_34px_80px_-20px_rgba(14,8,88,0.5)] flex flex-col'
                                  : 'shadow-[var(--cpm-shadow-modal)]',
                          ].join(' '),
                ].join(' ')}
                style={{
                    maxWidth,
                    marginTop: isPage || isDialog ? 0 : 32,
                    marginBottom: isPage || isDialog ? 0 : 32,
                    height: isPage ? 'calc(100vh - 64px)' : (height ?? undefined),
                    maxHeight: isPage ? 'calc(100vh - 64px)' : isDialog ? '100%' : undefined,
                    background: isPage ? 'var(--cpm-page-bg)' : '#fff',
                    boxShadow: isDialog ? 'var(--cpm-shadow-dialog)' : undefined,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body
    );
}
